import { api } from '../../shared/api/client'

export type SeoMarketplace = 'wb' | 'ozon' | 'ym'
export type SeoDesignChannelMarketplace = SeoMarketplace | 'site'
export type SeoDesignField = 'title' | 'description'

export type SeoTemplateOutputField = 'title' | 'annotation' | 'description'
export type SeoTemplateOperator = 'in' | 'not_in' | 'equals' | 'not_equals' | 'filled' | 'empty'

export interface SeoTemplateCondition {
  attribute: string
  operator: SeoTemplateOperator
  values: Array<string | number>
}

export interface SeoTemplateSelector {
  mode: 'all' | 'any'
  conditions: SeoTemplateCondition[]
}

export type SeoVisualBlock =
  | { id: string; type: 'literal'; label?: string; text: string; optional?: boolean }
  | { id: string; type: 'pim_attribute'; label?: string; attribute: string; optional?: boolean }
  | {
      id: string
      type: 'formatted_value'
      label?: string
      attribute: string
      format: 'age_years' | 'recipient_dative' | 'occasion_prepositional' | 'lowercase'
      prefix?: string
      suffix?: string
      optional?: boolean
    }
  | {
      id: string
      type: 'dictionary'
      label?: string
      dictionary: string
      key_attribute?: string
      key_value?: string
      key2_attribute?: string
      key2_value?: string
      slot: string
      segment?: number | '$segment'
      optional?: boolean
    }
  | {
      id: string
      type: 'conditional'
      label?: string
      condition: SeoTemplateCondition
      blocks: SeoVisualBlock[]
      optional?: boolean
    }
  | { id: string; type: 'seo_query'; label?: string; cluster_id?: number; optional?: boolean }
  | { id: string; type: 'paragraph_break'; label?: string; optional?: boolean }

/**
 * `Omit` по объединению схлопывает его в один тип с общими полями, и проверка
 * `block.type === 'dictionary'` перестаёт что-либо сужать: у результата нет ни
 * `dictionary`, ни `slot`. Раздача по членам объединения возвращает сужение —
 * без неё библиотека текстов работала на непроверенных типах.
 */
type WithoutId<T> = T extends unknown ? Omit<T, 'id'> : never

/** Блок палитры: то же, что блок шаблона, но ещё без идентификатора. */
export type SeoVisualBlockTemplate = WithoutId<SeoVisualBlock>

export interface SeoVisualOutput {
  blocks: SeoVisualBlock[]
  max_length?: number | null
}

export type SeoVisualOutputs = Partial<Record<SeoTemplateOutputField, SeoVisualOutput>>

export interface SeoGenerationTemplateVersion {
  id: number
  template_id: number
  template_name: string
  version: number
  status: 'draft' | 'active' | 'archived'
  marketplaces: SeoMarketplace[]
  seller_scope: 'all' | 'selected'
  seller_ids: number[]
  selector: SeoTemplateSelector
  outputs: SeoVisualOutputs
  priority: number
  notes: string | null
  revision: number
  created_by: number | null
  created_at: string
  updated_at: string
  activated_at: string | null
}

export interface SeoGenerationTemplateFamily {
  id: number
  name: string
  description: string | null
  created_by: number | null
  created_at: string
  updated_at: string
  archived_at: string | null
  version: SeoGenerationTemplateVersion | null
}

export interface SeoSelectorAttribute {
  key: string
  label: string
  path: string
  data_type: 'string' | 'number'
  operators: SeoTemplateOperator[]
  value_source: string
  allow_free_text: false
  group: 'product' | 'design' | 'audience' | 'event'
}

export interface SeoStandardBlock {
  key: string
  name: string
  group: 'Товар' | 'Дизайн' | 'Аудитория' | 'Событие' | 'SEO' | 'Служебные'
  description: string
  example: string
  block: SeoVisualBlockTemplate
  available: boolean
  unavailable_reason?: string
}

export interface SeoGenerationTemplateOptions {
  marketplaces: Array<{
    id: SeoMarketplace
    label: string
    outputs: Record<SeoTemplateOutputField, { supported: boolean; publishable: boolean; note?: string }>
  }>
  sellers: Array<{ id: number; marketplace: SeoMarketplace; label: string }>
  selector_attributes: SeoSelectorAttribute[]
  selector_values: {
    attribute: string | null
    items: Array<{ id: string; label: string }>
    total: number
  }
  categories: Array<{ id: number; prefix: string; label: string }>
  standard_blocks: SeoStandardBlock[]
  dictionaries: Array<{ id: string; label: string }>
  text_variants: SeoFragment[]
  text_variants_page?: {
    items: SeoFragment[]
    total: number
    limit: number
    offset: number
  }
  query_analytics: { available: boolean; reason: string }
}

export interface SeoGenerationTemplatePayload {
  name: string
  description?: string | null
  marketplaces: SeoMarketplace[]
  seller_scope: 'all' | 'selected'
  seller_ids: number[]
  selector: SeoTemplateSelector
  outputs: SeoVisualOutputs
  priority: number
  notes?: string | null
  revision?: number
  template_id?: number
  version_id?: number
  version?: number
  marketplace?: SeoMarketplace
  seller_id?: number
  article?: string
}

export interface SeoSettings {
  segment_count: number
}

export interface SeoTheme {
  theme_number: number
  theme_name: string | null
  designs: number
  products: number
  segments_assigned: number
}

/** Строка партии: товар с атрибутами макета и текущим опубликованным названием. */
export interface BatchCandidate {
  article: string
  design_code: string | null
  theme_number: number | null
  theme_name: string | null
  design_type: string | null
  category_prefix: string | null
  category_description: string | null
  gender: string | null
  age: string | null
  occasion: string | null
  image_subject: string | null
  image_text: string | null
  segment: number | null
  external_id: string | null
  published_title: string | null
}

export type IssueSeverity = 'critical' | 'warning' | 'tip'

export interface SeoIssue {
  code: string
  severity: IssueSeverity
  field?: string
  message: string
  reason?: string
  action?: string
  details?: Record<string, unknown>
}

export interface SeoDesignRegenerationChannel {
  marketplace: SeoDesignChannelMarketplace
  seller_id: number
}

export interface SeoDesignRegenerationPreviewRequest {
  design_code: string
  theme?: string
  channels: SeoDesignRegenerationChannel[]
  fields: SeoDesignField[]
}

export interface SeoDesignRegenerationPreviewRow {
  content_id: number
  article: string
  product_title: string | null
  marketplace: SeoDesignChannelMarketplace
  seller_id: number
  seller_name: string
  status: string
  expected_revision: number | null
  current: { title?: string; description?: string }
  proposed: { title?: string; description?: string }
  limits: { title: number | null; description: number | null }
  changed: boolean
  skipped_locked: string[]
  issues: SeoIssue[]
  blocked: boolean
}

export interface SeoDesignRegenerationPreview {
  design: {
    design_code: string
    theme: string
    theme_source: 'base_designs.theme' | 'base_designs.design_name'
    theme_number: number | null
    siblings: number
    design_name: string | null
    next_design_name: string | null
  }
  channels: Array<SeoDesignRegenerationChannel & { seller_name: string; has_contents: boolean }>
  products: number
  rows: SeoDesignRegenerationPreviewRow[]
  missing_contents: number
  summary: { total: number; changed: number; unchanged: number; blocked: number }
}

export interface SeoDesignRegenerationApplyRequest {
  design_code: string
  rows: Array<{
    content_id: number
    expected_revision: number | null
    fields: Partial<Record<SeoDesignField, string>>
  }>
  comment: string
  approve?: false
}

export interface SeoDesignRegenerationApplyResult {
  written: number
  written_rows: Array<{
    content_id: number
    article: string
    marketplace: SeoDesignChannelMarketplace
    seller_id: number
    revision: number
    locked_skipped: string[]
  }>
  skipped: Array<{ content_id: number; reason: string }>
  approved: number
}

export interface GeneratedFields {
  title?: string
  annotation?: string
  description?: string
  characteristics?: Record<string, string>
}

export interface FieldOutcome {
  field: string
  text: string
  generator_id: number | null
  generator_version: number | null
  fragments: Array<{ id: number | null; kind: string; slot: string; text: string }>
  facts: Array<{ path: string; value: string }>
  issues: SeoIssue[]
  blocked: boolean
}

export interface GenerationResult {
  article: string
  design_code: string | null
  marketplace: SeoMarketplace
  seller_id: number
  segment: number
  fields: GeneratedFields
  field_sources: Record<string, SeoFieldSource>
  text_profile?: SeoTextProfile | null
  text_validation?: SeoTextValidation
  outcomes: FieldOutcome[]
  keys: Record<string, string>
  key_resolutions: Array<{ key_type: string; value: string | null; rule_name: string | null }>
  media: { set_id: number | null; urls: string[] }
  characteristics: Record<string, string[]>
  characteristic_limits: Array<{ characteristic: string; count: number; max: number }>
  issues: SeoIssue[]
  blocked: boolean
}

export interface BatchGenerationResult {
  marketplace: SeoMarketplace
  seller_id: number
  total: number
  generated: number
  blocked: number
  items: GenerationResult[]
  errors: Array<{ article: string; message: string }>
  /** Сводка по длине названия — аналог строки с MAX() в исходных таблицах. */
  title_length: { max: number; over_limit: number; limit: number | null }
}

export interface CoverageGap {
  kind: string
  slot: string
  key_type?: string
  title?: string
  seller_id: number
  missing: string[]
  expected: number
  covered?: number
  missing_mappings?: number
  missing_fragments?: number
  items?: Array<{
    source_value: string
    resolved_key: string | null
    rule_id: number | null
    rule_name: string | null
    status: 'covered' | 'missing_mapping' | 'missing_fragment'
  }>
}

export interface SeoFragment {
  id: number
  version?: number
  family_id?: number
  marketplace: string
  seller_id: number
  kind: string
  key1: string
  key2: string
  slot: string
  segment: number
  text: string
  ai_editable?: boolean
  status?: string
  notes?: string | null
}

export interface SeoGenerator {
  id: number
  marketplace: SeoMarketplace
  field: string
  scope_type: string
  scope_value: string
  seller_id: number
  version: number
  status: 'draft' | 'active' | 'archived'
  name: string
  recipe: unknown
  notes: string | null
}

export interface SeoRuleset {
  id: number
  marketplace: SeoMarketplace
  scope_type: 'global' | 'mp_category' | 'pim_category'
  scope_value: string
  version: number
  status: 'draft' | 'active' | 'archived'
  effective_from: string
  name: string
  rules: Record<string, unknown>
  notes?: string | null
}

export interface SeoKeyRule {
  id: number
  version?: number
  family_id?: number
  key_type: string
  marketplace: string
  priority: number
  conditions: unknown
  result_key: string
  name: string
  status: string
}

export type SeoConfigurationEntityType = 'fragment' | 'key_rule' | 'generator' | 'ruleset'

export interface SeoConfigurationRelease {
  id: number
  marketplace: SeoMarketplace
  seller_id: number
  name: string
  status: string
  notes?: string | null
  items_count?: number
  created_at?: string
  activated_at?: string
  items?: Array<{
    type: SeoConfigurationEntityType
    id: number
    previous_active_id: number | null
  }>
}

export interface SegmentSummaryRow {
  theme_number: number
  designs_total: number
  segments_assigned: number
  segment_1: number
  segment_2: number
  segment_3: number
  segment_4: number
}

export interface SeoSeller {
  seller_id: number
  seller_name: string | null
  seller_short_name: string | null
  cabinet_code: string | null
  marketplace: string
  active?: number | boolean
}

export interface SeoKeyword {
  id: number
  marketplace: SeoMarketplace
  phrase: string
  phrase_norm: string
  cluster_id: number | null
  cluster_name?: string | null
  intent: string | null
  keyword_type: string | null
  category_prefix: string | null
  source: string
  demand: number | null
  demand_updated_at: string | null
  competition: number | null
  seasonality: unknown
  status: 'active' | 'banned' | 'review'
  notes: string | null
  updated_at: string
}

export interface SeoKeywordCluster {
  id: number
  marketplace: SeoMarketplace
  name: string
  description: string | null
  keywords_count?: number
}

export interface SeoKeywordAssignment {
  id: number
  keyword_id: number
  phrase: string
  cluster_name: string | null
  intent: string | null
  keyword_type: string | null
  demand: number | null
  competition: number | null
  target_type: 'product' | 'design' | 'category' | 'theme'
  target_id: string
  marketplace: SeoMarketplace
  seller_id: number
  status: 'target' | 'additional' | 'irrelevant' | 'banned' | 'review'
  priority: number
  assigned_field: string | null
  relevance: number | null
  source: string
  updated_at: string
}

/** Строка импорта: фраза плюс всё, что дала выгрузка источника. */
export interface SeoKeywordImportRow {
  phrase: string
  demand?: number | null
  competition?: number | null
  cluster_name?: string | null
  keyword_type?: string | null
  intent?: string | null
  category_prefix?: string | null
}

export interface SeoKeywordImportResult {
  created: number
  updated: number
  skipped: number
  clusters_created: number
  with_demand: number
  errors: string[]
}

export interface SeoKeywordCoverageRow {
  keyword_id: number
  phrase: string
  cluster_id: number | null
  cluster_name: string | null
  demand: number | null
  statuses: string[]
  targets: Array<{ target_type: string; target_id: string; status: string; priority: number }>
  cards_in_scope: number
  cards_covered: number
  covered_fields: string[]
  never_covered: boolean
}

export interface SeoKeywordCoverageReport {
  marketplace: SeoMarketplace
  seller_id: number
  cards_analyzed: number
  cards_without_keywords: number
  keywords_total: number
  keywords_assigned: number
  keywords_unassigned: Array<{ keyword_id: number; phrase: string; cluster_name: string | null; demand: number | null }>
  keywords_never_covered: number
  items: SeoKeywordCoverageRow[]
  truncated: number
}

export interface SeoKeywordInput {
  id?: number
  marketplace: SeoMarketplace
  phrase: string
  cluster_id?: number | null
  intent?: string | null
  keyword_type?: string | null
  category_prefix?: string | null
  source?: string
  demand?: number | null
  competition?: number | null
  status?: 'active' | 'banned' | 'review'
  notes?: string | null
}

// ── Публикация ──────────────────────────────────────────────────────────────

export type SeoPublishField = 'title' | 'annotation' | 'description'

export type SeoPublishBatchStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled'

export type SeoPublishTaskStatus =
  | 'queued'
  | 'sending'
  | 'awaiting_moderation'
  | 'success'
  | 'partial'
  | 'failed'
  | 'skipped'

export type SeoModerationStatus = 'unknown' | 'pending' | 'accepted' | 'altered' | 'rejected'

export interface SeoPublishCapabilities {
  enabled: boolean
  allowed_seller_ids: number[]
  /** Партия пройдёт весь путь, но наружу ничего не отправит. */
  dry_run: boolean
  dry_run_reason: string | null
  fields: SeoPublishField[]
  note: string
}

export interface SeoModuleCapabilities {
  publishing: boolean
  analytics: boolean
  experiments: boolean
  keywords: boolean
}

export interface SeoPublishCandidate {
  article: string
  content_id: number | null
  revision_id: number | null
  fields: SeoPublishField[]
  values: { title?: string; description?: string; annotation?: string }
  publishable: boolean
  skip_reason: string | null
  skip_message: string | null
}

export interface SeoPublishPreview {
  total: number
  publishable: number
  skipped: number
  dry_run: boolean
  dry_run_reason: string | null
  items: SeoPublishCandidate[]
}

export interface SeoPublishBatch {
  id: number
  batch_uid: string
  mode: 'single' | 'bulk'
  marketplace: SeoMarketplace | null
  seller_id: number | null
  params: { fields: SeoPublishField[]; articles?: string[]; comment?: string | null; force?: boolean }
  status: SeoPublishBatchStatus
  scheduled_at: string | null
  dry_run: boolean
  cancel_requested: boolean
  total: number
  succeeded: number
  failed: number
  skipped: number
  error: string | null
  created_by: number | null
  created_at: string
  finished_at: string | null
}

export interface SeoPublishTask {
  id: number
  batch_id: number
  content_id: number
  revision_id: number
  idempotency_key: string
  status: SeoPublishTaskStatus
  attempt: number
  external_task_id: string | null
  moderation_status: SeoModerationStatus
  next_attempt_at: string | null
  field_results: Record<string, { status: string; message?: string }> | null
  error: string | null
  error_code: string | null
  started_at: string | null
  completed_at: string | null
  article: string
  marketplace: SeoMarketplace
  seller_id: number
  external_id: string | null
}

export interface SeoPublishSummary {
  batches: number
  running: number
  scheduled: number
  published_contents: number
  awaiting_moderation: number
  rejected: number
  altered: number
}

export interface SeoCardPublishState {
  content_id: number
  sync_state: string
  status: string
  published_revision_id: number | null
  moderation_status: SeoModerationStatus
  moderation_message: string | null
  confirmed_revision_id: number | null
  moderation_checked_at: string | null
  last_task: {
    id: number
    batch_id: number
    status: SeoPublishTaskStatus
    error: string | null
    completed_at: string | null
  } | null
  dry_run: boolean
}

export type SeoCenterState = 'critical' | 'discrepancy' | 'review' | 'draft' | 'ready' | 'published' | 'new'

export interface SeoCenterItem {
  article: string
  product_title: string | null
  marketplace: SeoMarketplace
  seller_id: number
  external_id: string | null
  theme_number: number | null
  theme_name: string | null
  category_prefix: string | null
  category_name: string | null
  content_id: number | null
  content_status: string | null
  sync_state: string
  state: SeoCenterState
  critical_count: number
  warning_count: number
  top_issue_code: string | null
  next_action_code: string | null
  draft_revision_id: number | null
  published_revision_id: number | null
  /** Момент создания текущей версии текста; null — текста ещё не было. */
  text_updated_at: string | null
}

/** Порядок очереди: по важности, по давности изменения текста, по свежести. */
export type SeoCenterSort = 'priority' | 'stale' | 'recent' | 'potential'

export interface SeoCenterSummary {
  total: number
  critical: number
  review: number
  draft: number
  ready: number
  published: number
  discrepancy: number
  unassessed: number
}

export interface SeoProblemGroup {
  key: 'critical' | 'discrepancy' | 'review' | 'draft'
  title: string
  action: string
  count: number
}

export interface SeoCenterResponse {
  items: SeoCenterItem[]
  total: number
  summary: SeoCenterSummary
  problems: SeoProblemGroup[]
  scope: { marketplace: SeoMarketplace; seller_id: number }
}

/** Счётчики панели фильтров очереди: карточки на префикс категории. */
export interface SeoCenterFacets {
  category_prefix: Record<string, number>
  total: number
}

export interface SeoCardOverview {
  content: {
    id: number
    article: string
    marketplace: SeoMarketplace
    seller_id: number
    mp_category_id: string | null
    status: string
    sync_state: string
    external_id: string | null
    draft_revision_id: number | null
    published_revision_id: number | null
  }
  seller: { seller_id: number; marketplace: SeoMarketplace; name: string | null; cabinet_code: string | null }
  product: { article: string; title: string | null; design_code: string | null; category: string | null; category_prefix: string | null }
  ruleset: { id: number; name: string; version: number; scope_type: string; scope_value: string } | null
  fields: SeoContentFields
  draft: SeoRevision | null
  published: SeoRevision | null
  facts: Array<{ key: string; label: string; value: string; source: string }>
  keywords: Array<{ keyword_id: number; phrase: string; covered: boolean; covered_in: string[]; inherited_from: string | null }>
  validation: {
    issues: SeoCardIssue[]
    critical: number
    warnings: number
    tips: number
    publishable: boolean
    text_approvable?: boolean
    approval_blockers?: number
    readiness_blockers?: number
    approval_mode?: 'full_card' | 'text_revision'
  }
  scores: {
    completeness: number | null
    relevance: number | null
    quality: number | null
    effectiveness: number | null
  }
  actions: SeoPriorityAction[]
  diff: SeoFieldDiff[]
  locks: SeoContentLock[]
  revisions: SeoRevision[]
}

export interface SeoStoredMarketplaceCategoryOption {
  id: string
  label: string
  pim_category_id: number
  pim_category_prefix: string
}

export interface SeoContentFields {
  title?: string
  annotation?: string
  description?: string
  characteristics?: Record<string, string | number | string[]>
}

export interface SeoFieldSource {
  source: 'manual' | 'template' | 'ai' | 'marketplace' | 'generator' | 'regenerated'
  ref?: string
  inherited_from?: string
  profile?: { id: string; version_id: number; version: number; locale: string }
  template?: { family_id: number; version_id: number; version: number; output: string }
  fragments?: Array<{ id: number; family_id: number | null; version: number | null; ref: string }>
  facts?: Array<{ path: string; value: string; source: 'pim' }>
  /** Фразы семантического ядра, подставленные блоком «Поисковый запрос». */
  keywords?: Array<{
    keyword_id: number
    phrase: string
    cluster_id: number | null
    cluster_name: string | null
    target_type: string
    target_id: string
    position: number
    eligible_count: number
    block_id?: string
  }>
  variation?: SeoTextVariationProvenance
  variations?: SeoTextVariationProvenance[]
  /** Временные кампании, наложенные поверх шаблона: без них текст необъясним. */
  campaigns?: Array<{
    id: number
    name: string
    mode: 'append' | 'prepend' | 'replace'
    block_id: string
    replaced_block_id?: string
  }>
  fallback?: { used: boolean; reason?: string }
}

export interface SeoTextVariationProvenance {
  source: 'persisted_design_segment' | 'deterministic_derived_segment'
  algorithm_version: string
  profile_id: string
  block_id: string
  stable_variant_id: string
  eligible_variant_count: number
  segment: number
  db_write: false
}

export type SeoTextValidationSeverity = 'blocker' | 'warning' | 'info'

export interface SeoTextPolicySource {
  source: string
  verified_at: string
  contract: string
}

export interface SeoTextFieldPolicy {
  required: boolean
  max_length: number | null
  source: SeoTextPolicySource
  note?: string
}

export interface SeoTextProfileOutput {
  field: 'title' | 'description'
  blocks: Array<Record<string, unknown> & { id?: string; type?: string }>
  block_provenance: Array<{
    block_id: string
    block_type: string
    source: 'generation_template'
    content_source: 'pim' | 'versioned_fragment' | 'approved_literal' | 'template_control'
  }>
  policy: SeoTextFieldPolicy
}

export interface SeoTextProfile {
  id: string
  version_id: number
  version: number
  name: string
  activated_at: string | null
  marketplace: SeoMarketplace
  category_prefix: string
  locale: string
  composition: 'hybrid'
  fact_owner: 'pim'
  structure_owner: 'generation_template'
  variation_source: 'persisted_design_segment_or_deterministic_derived_segment'
  outputs: Partial<Record<'title' | 'description', SeoTextProfileOutput>>
}

export interface SeoTextValidationIssue {
  code: string
  severity: SeoTextValidationSeverity
  field?: 'title' | 'description'
  message: string
  remediation_hint: string
  rule_source: string
  details?: Record<string, unknown>
  group?: SeoCardIssue['group']
  scope?: string
  blocks?: SeoCardIssue['blocks']
  remediation?: SeoCardIssue['remediation']
}

export interface SeoTextValidation {
  issues: SeoTextValidationIssue[]
  blockers: number
  warnings: number
  info: number
  approvable: boolean
  policies: SeoTextProfile['outputs'] | null
}

export interface SeoCardValidationResponse {
  validation: SeoCardOverview['validation']
  scores: SeoCardOverview['scores']
  actions: SeoPriorityAction[]
  diff: SeoFieldDiff[]
}

export interface SeoCardGenerationPreview {
  fields: SeoContentFields
  sources: Record<string, SeoFieldSource>
  text_profile?: SeoTextProfile | null
  text_validation?: SeoTextValidation
  diff: SeoFieldDiff[]
  issues: SeoCardIssue[]
  missing_variables: string[]
  skipped_locked: string[]
  outcomes: FieldOutcome[]
  blocked: boolean
  saved: false
}

export interface SeoRevision {
  id: number
  content_id: number
  revision: number
  kind: 'draft' | 'published' | 'remote_snapshot'
  fields: SeoContentFields
  field_sources: Record<string, SeoFieldSource>
  is_ai: boolean
  comment: string | null
  created_by: number | null
  created_at: string
  published_at: string | null
}

export interface SeoRevisionComparison {
  from: Pick<SeoRevision, 'id' | 'revision' | 'kind' | 'created_at'>
  to: Pick<SeoRevision, 'id' | 'revision' | 'kind' | 'created_at'>
  diff: SeoFieldDiff[]
}

export interface SeoContentLock {
  id: number
  content_id: number
  field: string
  fragment: string | null
  reason: string | null
}

export interface SeoCardIssue {
  code: string
  severity: IssueSeverity
  field?: string
  message: string
  reason?: string
  action?: string
  group?: 'content' | 'pim_data' | 'generation_config' | 'marketplace_readiness' | 'system'
  scope?: string
  blocks?: 'text_approval' | 'marketplace_readiness' | 'none'
  remediation?: {
    type: 'edit_field' | 'edit_pim_attribute' | 'open_profile' | 'open_fragment' | 'open_marketplace_category' | 'regenerate_field' | 'review_only'
    target?: {
      kind: 'field' | 'pim_attribute' | 'profile' | 'fragment' | 'marketplace_category' | 'checks'
      field?: string
      path?: string
    } | null
    automatic?: boolean
  }
  details?: Record<string, unknown>
}

export interface SeoPriorityAction {
  code: string
  problem: string
  reason: string
  field?: string
  priority: 'high' | 'medium' | 'low'
  action: string
  expected_effect: string
  confidence: 'high' | 'medium' | 'low'
}

export interface SeoFieldDiff {
  field: string
  before: string
  after: string
}

export interface SeoBatchSelection {
  mode: 'filter' | 'explicit' | 'campaign'
  articles?: string[]
  campaign?: { id: number; phase: 'apply' | 'revert' }
  filters?: {
    theme_number?: number | null
    category_prefix?: string | null
    design_type?: string | null
    brand?: string | null
    occasion?: string | null
    gender?: string | null
    age?: string | null
    /** Трёхзначно: не задан — не фильтруем, true — заполнено, false — пусто. */
    has_age_on_image?: boolean
    has_name_on_image?: boolean
    search?: string | null
    only_unpublished?: boolean
  }
}

export interface SeoBatchFilterOption {
  value: string
  label: string
  designs: number
}

/** Значения атрибутов макета для выпадающих списков отбора партии. */
export interface SeoBatchFilterOptions {
  categories: SeoBatchFilterOption[]
  design_types: SeoBatchFilterOption[]
  brands: SeoBatchFilterOption[]
  occasions: SeoBatchFilterOption[]
  genders: SeoBatchFilterOption[]
  ages: SeoBatchFilterOption[]
}

export interface SeoBatchOptions {
  seasonal?: boolean
  duplicate?: boolean
  fields?: Array<'title' | 'annotation' | 'description'>
  title_limit?: number | null
  comment?: string | null
}

export type SeoBatchJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled'

export interface SeoBatchJobIssue {
  id: number
  article: string
  status: 'blocked' | 'generation_error' | 'save_error'
  message: string | null
  details: unknown
}

export interface SeoBatchJob {
  id: string
  user_id: number
  seller_id: number
  marketplace: SeoMarketplace
  selection_mode: SeoBatchSelection['mode']
  selection: SeoBatchSelection
  options: SeoBatchOptions
  status: SeoBatchJobStatus
  total: number
  processed: number
  generated: number
  saved: number
  blocked: number
  failed: number
  cancel_requested: boolean
  error: string | null
  started_at: string | null
  completed_at: string | null
  /** Заполнено — результаты задания уже отменены массовым откатом. */
  rolled_back_at: string | null
  created_at: string
  updated_at: string
  issues?: SeoBatchJobIssue[]
}

/** Что вернёт массовый откат — показывается до нажатия, а не после. */
export interface SeoBatchRollbackPreview {
  restorable: number
  without_previous: number
  changed_since: number
  already_rolled_back: boolean
}

export interface SeoBatchRollbackResult {
  job_id: string
  restored: number
  skipped: Array<{ article: string; reason: string }>
  changed_since: number
}

/** Карточка, текст которой собран раньше последнего изменения товара или макета. */
export interface SeoStaleContent {
  content_id: number
  article: string
  design_code: string | null
  category_prefix: string | null
  text_updated_at: string
  data_updated_at: string
  days_behind: number
}

// ── Кампании ────────────────────────────────────────────────────────

export type SeoCampaignStatus = 'draft' | 'scheduled' | 'running' | 'finished' | 'archived'
export type SeoCampaignChangeMode = 'append' | 'prepend' | 'replace'

export interface SeoCampaignChange {
  field: 'title' | 'annotation' | 'description'
  mode: SeoCampaignChangeMode
  target_block_id?: string
  block: SeoVisualBlock
}

export interface SeoCampaign {
  id: number
  organization_id: number
  name: string
  description: string | null
  status: SeoCampaignStatus
  starts_on: string
  ends_on: string
  marketplaces: SeoMarketplace[]
  seller_scope: 'all' | 'selected'
  seller_ids: number[]
  selector: SeoTemplateSelector
  articles: string[]
  changes: SeoCampaignChange[]
  priority: number
  auto_apply: boolean
  auto_revert: boolean
  created_by: number | null
  created_at: string
  updated_at: string
  archived_at: string | null
  /** Где кампания находится относительно сегодняшнего дня. */
  phase?: 'before' | 'during' | 'after'
}

export interface SeoCampaignRun {
  id: number
  campaign_id: number
  phase: 'apply' | 'revert'
  marketplace: SeoMarketplace
  seller_id: number
  job_id: string | null
  status: 'queued' | 'skipped' | 'failed'
  total: number
  message: string | null
  created_at: string
}

export interface SeoCampaignDetails {
  campaign: SeoCampaign
  phase: 'before' | 'during' | 'after'
  channels: Array<{ marketplace: SeoMarketplace; seller_id: number; label: string }>
  runs: SeoCampaignRun[]
  started_runs?: SeoCampaignRun[]
}

export interface SeoCampaignPayload {
  name: string
  description?: string | null
  starts_on: string
  ends_on: string
  marketplaces: SeoMarketplace[]
  seller_scope: 'all' | 'selected'
  seller_ids: number[]
  selector: SeoTemplateSelector
  articles: string[]
  changes: SeoCampaignChange[]
  priority?: number
  auto_apply?: boolean
  auto_revert?: boolean
}

export interface SeoCampaignPreviewSample {
  article: string
  base: { title?: string; annotation?: string; description?: string }
  during: { title?: string; annotation?: string; description?: string }
  after: { title?: string; annotation?: string; description?: string }
  reverts: boolean
  blocked: boolean
  issues: Array<{ code: string; severity: string; message: string }>
}

export interface SeoCampaignPreview {
  total: number
  channels: Array<{ marketplace: SeoMarketplace; seller_id: number; label: string }>
  scope_channel: { marketplace: SeoMarketplace; seller_id: number; label: string } | null
  samples: SeoCampaignPreviewSample[]
  during_date: string
  after_date: string
}

export interface SeoAiBatchOptions {
  fields: Array<'title' | 'annotation' | 'description'>
  budget_usd: number
  max_items: number
  strategy: 'all' | 'low_quality' | 'with_warnings'
  quality_threshold: number
  comment?: string | null
}

export type SeoAiBatchJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'budget_exhausted'
  | 'failed'
  | 'cancelled'

export type SeoAiBatchResultStatus =
  | 'ready'
  | 'applying'
  | 'blocked'
  | 'skipped'
  | 'accepted'
  | 'rejected'
  | 'stale'
  | 'error'

export interface SeoAiBatchResult {
  id: number
  job_id: string
  content_id: number | null
  article: string
  status: SeoAiBatchResultStatus
  source_revision: number | null
  original_fields: SeoContentFields
  base_fields: SeoContentFields
  suggested_fields: SeoContentFields
  field_sources: Record<string, SeoFieldSource>
  diff: SeoFieldDiff[]
  validation: {
    issues: SeoCardIssue[]
    critical: number
    warnings: number
    tips: number
    publishable: boolean
  } | null
  provider: string | null
  model: string | null
  confidence: 'high' | 'medium' | 'low' | null
  rationale: string | null
  tokens_in: number | null
  tokens_out: number | null
  cost_usd: number
  cost_estimated: boolean
  error: string | null
  revision_id: number | null
}

export interface SeoAiBatchJob {
  id: string
  user_id: number
  seller_id: number
  marketplace: SeoMarketplace
  selection_mode: 'filter' | 'explicit'
  selection: SeoBatchSelection
  options: SeoAiBatchOptions
  status: SeoAiBatchJobStatus
  total: number
  processed: number
  ready: number
  blocked: number
  skipped: number
  failed: number
  accepted: number
  rejected: number
  tokens_in: number
  tokens_out: number
  estimated_cost_usd: number
  actual_cost_usd: number
  budget_usd: number
  provider: string
  model: string
  cancel_requested: boolean
  error: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
  results?: SeoAiBatchResult[]
  results_total?: number
}

export interface SeoAiBatchEstimate {
  selected: number
  total: number
  provider: string
  model: string
  estimated_tokens_per_item: { input: number; output: number }
  estimated_cost_per_item_usd: number
  estimated_cost_usd: number
  budget_usd: number
  estimated_items_within_budget: number
}

export type SeoAiAssistTask =
  | 'generate_variants'
  | 'fix_grammar'
  | 'shorten'
  | 'adapt_marketplace'
  | 'find_repeats'
  | 'check_naturalness'
  | 'alternative'
  | 'suggest_missing_block'
  | 'review_structure'
  | 'quality_review'

export interface SeoAiAssistSuggestion {
  id?: string
  label?: string
  text?: string
  field?: SeoTemplateOutputField | string
  rationale?: string
  blocks?: SeoVisualBlock[]
}

export interface SeoAiAssistIssue {
  code?: string
  severity?: 'critical' | 'warning' | 'tip'
  message: string
  field?: SeoTemplateOutputField | string
  block_id?: string
  suggestion?: string
}

/**
 * Поля для промпта: либо готовые значения карточки, либо список названий полей.
 * `GeneratedFields` указан явно — у интерфейса без индексной подписи нет
 * совместимости с `Record<string, unknown>`, и предпросмотр шаблона не проходил
 * проверку типов.
 */
export type SeoAiFieldsPayload = GeneratedFields | Record<string, unknown> | string[]

export interface SeoAiAssistRequest {
  task: SeoAiAssistTask
  scope: string | Record<string, unknown>
  marketplace: SeoMarketplace
  current_text: string
  fields?: SeoAiFieldsPayload
  context?: Record<string, unknown>
}

export interface SeoAiAssistResponse {
  suggestions: SeoAiAssistSuggestion[]
  issues: SeoAiAssistIssue[]
  provider: string
  model: string
  confidence: 'high' | 'medium' | 'low'
  rationale?: string | null
  usage?: { tokens_in?: number | null; tokens_out?: number | null }
  saved: false
}

const unwrap = <T,>(response: { data?: unknown }): T => {
  const payload = response.data

  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }

  return payload as T
}

/** Списки отправляются одной строкой через запятую; пустой список не уходит вовсе. */
function withJoinedListParams<T extends Record<string, unknown>>(params: T): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      if (value.length) result[key] = value.join(',')
      continue
    }
    result[key] = value
  }
  return result
}

/** mp_sellers хранит «wildberries»/«ozon»/«yandex_market» — модуль работает с короткими кодами. */
export function toShortMarketplace(value: string | null | undefined): SeoMarketplace | null {
  const normalized = String(value || '').toLowerCase()
  if (normalized.startsWith('wild') || normalized === 'wb') return 'wb'
  if (normalized.startsWith('oz')) return 'ozon'
  if (normalized === 'ym' || normalized.startsWith('yandex')) return 'ym'
  return null
}

export interface SeoTemplateCoverageCell {
  seller_id: number
  seller_name: string | null
  field: 'title' | 'description'
  template_id: number | null
  template_name: string | null
  template_version: number | null
  conflict: boolean
}

export interface SeoTemplateCoverageRow {
  marketplace: SeoMarketplace
  category_prefix: string
  category_name: string | null
  products: number
  cells: SeoTemplateCoverageCell[]
  covered: number
  total: number
}

export interface SeoTemplateCoverage {
  rows: SeoTemplateCoverageRow[]
  summary: { categories: number; fully_covered: number; uncovered_products: number; conflicts: number }
}
export const seoAPI = {
  getModuleCapabilities: async (): Promise<SeoModuleCapabilities> =>
    unwrap(await api.get('/seo/capabilities')),

  listGenerationTemplates: async (params: {
    q?: string
    marketplace?: SeoMarketplace
    seller_id?: number
    category_prefix?: string
    status?: string
    output?: SeoTemplateOutputField
    sort?: string
    limit?: number
    offset?: number
  } = {}): Promise<{ items: SeoGenerationTemplateFamily[]; total: number }> =>
    unwrap(await api.get('/seo/generation-templates', { params })),

  getGenerationTemplateOptions: async (params: {
    attribute?: string
    q?: string
    limit?: number
    offset?: number
    fragment_q?: string
    fragment_kind?: string
    fragment_slot?: string
    fragment_marketplace?: SeoMarketplace
    fragment_limit?: number
    fragment_offset?: number
  } = {}): Promise<SeoGenerationTemplateOptions> =>
    unwrap(await api.get('/seo/generation-templates/options', { params })),

  getGenerationTemplate: async (id: number, versionId?: number): Promise<{
    family: SeoGenerationTemplateFamily
    versions: SeoGenerationTemplateVersion[]
  }> => unwrap(await api.get(`/seo/generation-templates/${id}`, {
    params: versionId ? { version_id: versionId } : undefined,
  })),

  createGenerationTemplate: async (payload: SeoGenerationTemplatePayload): Promise<SeoGenerationTemplateFamily> =>
    unwrap(await api.post('/seo/generation-templates', payload)),

  updateGenerationTemplate: async (
    id: number,
    versionId: number,
    payload: SeoGenerationTemplatePayload,
  ): Promise<SeoGenerationTemplateFamily> =>
    unwrap(await api.put(`/seo/generation-templates/${id}/versions/${versionId}`, payload)),

  duplicateGenerationTemplate: async (id: number): Promise<SeoGenerationTemplateFamily> =>
    unwrap(await api.post(`/seo/generation-templates/${id}/duplicate`)),

  newGenerationTemplateVersion: async (id: number): Promise<SeoGenerationTemplateFamily> =>
    unwrap(await api.post(`/seo/generation-templates/${id}/new-version`)),

  activateGenerationTemplate: async (id: number, versionId: number): Promise<SeoGenerationTemplateVersion> =>
    unwrap(await api.post(`/seo/generation-templates/${id}/activate`, { version_id: versionId })),

  archiveGenerationTemplate: async (id: number): Promise<{ archived: true }> =>
    unwrap(await api.post(`/seo/generation-templates/${id}/archive`)),

  previewGenerationTemplateMatch: async (payload: SeoGenerationTemplatePayload): Promise<{
    total: number
    samples: Array<{
      article: string
      category: string | null
      category_prefix: string | null
      design_type: string | null
      gender: string | null
      persona: string | null
      age_on_image: number | null
      occasion: string | null
    }>
    missing: Record<string, number>
    missing_total: number
    intersections: Array<{ template_id: number; name: string }>
    conflicts: Array<{
      template_id: number
      name: string
      version: number
      priority: number
      outputs: string[]
      message: string
    }>
  }> => unwrap(await api.post('/seo/generation-templates/match-preview', payload)),

  previewGenerationTemplateContent: async (payload: SeoGenerationTemplatePayload): Promise<{
    sample: { article: string; [key: string]: unknown }
    result: GenerationResult
    lengths: Record<string, number>
  }> => unwrap(await api.post('/seo/generation-templates/preview', payload)),

  /**
   * Кабинеты для выбора. Показываем только активные: в mp_sellers есть
   * демо-запись и две устаревшие строки от перевыпуска доступов.
   */
  listSellers: async (): Promise<SeoSeller[]> => {
    const data = unwrap<SeoSeller[] | { items: SeoSeller[] }>(await api.get('/sellers'))
    const rows = Array.isArray(data) ? data : data?.items ?? []
    return rows.filter((seller) => seller.active === undefined || Number(seller.active) === 1)
  },

  // Раздел «Запросы» скрыт флагом, методы оставлены на месте:
  // SPEC_2026-08-13_SEO_MODULE_SLIMMING.md.
  listKeywords: async (params: {
    marketplace?: SeoMarketplace
    status?: string
    cluster_id?: number
    intent?: string
    keyword_type?: string
    category_prefix?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{ items: SeoKeyword[]; total: number }> => unwrap(await api.get('/seo/keywords', { params })),

  saveKeyword: async (payload: SeoKeywordInput): Promise<SeoKeyword> => unwrap(await api.post('/seo/keywords', payload)),

  deleteKeyword: async (id: number): Promise<{ deleted: true }> => unwrap(await api.delete(`/seo/keywords/${id}`)),

  importKeywords: async (payload: { marketplace: SeoMarketplace; items: SeoKeywordImportRow[]; demand_date?: string | null }): Promise<SeoKeywordImportResult> => unwrap(await api.post('/seo/keywords/import', payload)),

  getKeywordCoverage: async (params: { marketplace: SeoMarketplace; seller_id: number; limit?: number }): Promise<SeoKeywordCoverageReport> => unwrap(await api.get('/seo/keywords/coverage', { params })),

  listKeywordClusters: async (marketplace?: SeoMarketplace): Promise<SeoKeywordCluster[]> => {
    const data = unwrap<{ items: SeoKeywordCluster[] }>(await api.get('/seo/clusters', { params: { marketplace } }))
    return data.items
  },

  saveKeywordCluster: async (payload: { id?: number; marketplace: SeoMarketplace; name: string; description?: string | null }): Promise<SeoKeywordCluster> => unwrap(await api.post('/seo/clusters', payload)),

  listKeywordAssignments: async (params: {
    marketplace?: SeoMarketplace
    seller_id?: number
    target_type?: string
    target_id?: string
    status?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{ items: SeoKeywordAssignment[]; total: number }> => unwrap(await api.get('/seo/keywords/assignments', { params })),

  assignKeywords: async (payload: {
    keyword_ids: number[]
    target_type: 'product' | 'design' | 'category' | 'theme'
    target_id: string
    marketplace: SeoMarketplace
    seller_id: number
    status?: SeoKeywordAssignment['status']
    priority?: number
    assigned_field?: string | null
    relevance?: number | null
    source?: string
  }): Promise<{ assigned: number }> => unwrap(await api.post('/seo/keywords/assign', payload)),

  removeKeywordAssignment: async (id: number): Promise<{ deleted: true }> => unwrap(await api.delete(`/seo/keywords/assignments/${id}`)),

  getCenter: async (params: {
    marketplace: SeoMarketplace
    seller_id: number
    q?: string
    state?: SeoCenterState | 'attention'
    sync_state?: string | string[]
    theme_number?: number | number[]
    category_prefix?: string | string[]
    /** «Текст не менялся более N дней»: только карточки, у которых текст есть. */
    stale_days?: number
    sort?: SeoCenterSort
    limit?: number
    offset?: number
  }): Promise<SeoCenterResponse> =>
    unwrap(await api.get('/seo/center', { params: withJoinedListParams(params) })),

  /** Счётчики панели фильтров очереди; фильтр категории сервер исключает сам. */
  getCenterFacets: async (params: {
    marketplace: SeoMarketplace
    seller_id: number
    q?: string
    state?: SeoCenterState | 'attention'
    category_prefix?: string | string[]
    theme_number?: number | number[]
    sync_state?: string | string[]
    stale_days?: number
  }): Promise<SeoCenterFacets> =>
    unwrap(await api.get('/seo/center/facets', { params: withJoinedListParams(params) })),

  /** Согласование выборки из очереди: непроходные карточки возвращаются с причиной. */
  approveDrafts: async (payload: {
    marketplace: SeoDesignChannelMarketplace
    seller_id: number
    articles: string[]
  }): Promise<{
    requested: number
    approved: number
    approved_articles: string[]
    skipped: Array<{ article: string; reason: string }>
  }> => unwrap(await api.post('/seo/cards/approve', payload)),

  getCard: async (params: { article: string; marketplace: SeoMarketplace; seller_id: number }): Promise<SeoCardOverview> =>
    unwrap(await api.get('/seo/card', { params })),

  setCardMarketplaceCategory: async (payload: { content_id: number; mp_category_id: string }): Promise<SeoCardOverview['content']> =>
    unwrap(await api.post('/seo/card/mp-category', payload)),

  /**
   * Использует только сохранённые сопоставления PIM-категорий. Для WB это
   * base_categories.subjectID; внешние API площадок здесь не вызываются.
   */
  listStoredMarketplaceCategoryOptions: async (params: {
    marketplace: SeoMarketplace
    pim_category_prefix?: string | null
  }): Promise<SeoStoredMarketplaceCategoryOption[]> => {
    if (params.marketplace !== 'wb') return []
    const payload = unwrap<unknown>(await api.get('/categories'))
    const rows = Array.isArray(payload)
      ? payload
      : payload && typeof payload === 'object' && 'items' in payload && Array.isArray((payload as { items?: unknown }).items)
        ? (payload as { items: unknown[] }).items
        : []
    const preferredPrefix = String(params.pim_category_prefix || '').toUpperCase()
    const seen = new Set<string>()
    return rows
      .map((raw) => raw as { category_id?: unknown; prefix?: unknown; description?: unknown; subjectID?: unknown })
      .filter((row) => Number(row.subjectID) > 0 && Number(row.category_id) > 0)
      .map((row) => ({
        id: String(Number(row.subjectID)),
        label: `${String(row.prefix || 'Категория')} — ${String(row.description || 'Без названия')} (ID ${Number(row.subjectID)})`,
        pim_category_id: Number(row.category_id),
        pim_category_prefix: String(row.prefix || ''),
      }))
      .sort((left, right) => {
        const leftPreferred = left.pim_category_prefix.toUpperCase() === preferredPrefix ? 0 : 1
        const rightPreferred = right.pim_category_prefix.toUpperCase() === preferredPrefix ? 0 : 1
        return leftPreferred - rightPreferred || left.label.localeCompare(right.label, 'ru')
      })
      .filter((option) => {
        if (seen.has(option.id)) return false
        seen.add(option.id)
        return true
      })
  },

  saveCardDraft: async (payload: {
    content_id: number
    fields: SeoContentFields
    expected_revision?: number | null
    comment?: string
    field_sources?: Record<string, SeoFieldSource>
    is_ai?: boolean
    ai_meta?: Record<string, unknown>
  }): Promise<unknown> => unwrap(await api.post('/seo/card/draft', payload)),

  validateCard: async (payload: { content_id: number; fields?: SeoContentFields; persist_scores?: boolean }): Promise<SeoCardValidationResponse> =>
    unwrap(await api.post('/seo/card/validate', payload)),

  previewCardGeneration: async (payload: {
    content_id: number
    fields: Array<'title' | 'description'>
    current_fields?: SeoContentFields
  }): Promise<SeoCardGenerationPreview> => unwrap(await api.post('/seo/card/generate-preview', payload)),

  previewDesignRegeneration: async (
    payload: SeoDesignRegenerationPreviewRequest,
  ): Promise<SeoDesignRegenerationPreview> =>
    unwrap(await api.post('/seo/preview', { scope: 'design', ...payload })),

  applyDesignRegeneration: async (
    payload: SeoDesignRegenerationApplyRequest,
  ): Promise<SeoDesignRegenerationApplyResult> =>
    unwrap(await api.post('/seo/design/regenerate', payload)),

  getAiCapabilities: async (): Promise<{
    enabled: boolean
    feature_enabled: boolean
    configured: boolean
    provider: string | null
    model: string | null
    mode: 'preview_only'
    auto_save: false
    auto_publish: false
    allowed_fields: string[]
    assist_tasks: SeoAiAssistTask[]
  }> => unwrap(await api.get('/seo/ai/capabilities')),

  suggestCardWithAi: async (payload: {
    content_id: number
    fields?: string[]
    current_fields?: SeoContentFields
  }): Promise<{
    fields: SeoContentFields
    sources: Record<string, SeoFieldSource>
    provider: string
    model: string
    rationale: string | null
    confidence: 'high' | 'medium' | 'low'
    usage: { tokens_in: number | null; tokens_out: number | null }
    diff: SeoFieldDiff[]
    validation: { issues: SeoCardIssue[]; critical: number; warnings: number; tips: number; publishable: boolean }
    saved: false
  }> => unwrap(await api.post('/seo/card/ai-suggest', payload)),

  assistWithAi: async (payload: SeoAiAssistRequest): Promise<SeoAiAssistResponse> =>
    unwrap(await api.post('/seo/ai/assist', payload)),

  approveCardDraft: async (content_id: number): Promise<unknown> =>
    unwrap(await api.post('/seo/card/approve', { content_id })),

  addCardLock: async (payload: { content_id: number; field: string; fragment?: string; reason?: string }): Promise<SeoContentLock> =>
    unwrap(await api.post('/seo/card/locks', payload)),

  removeCardLock: async (id: number): Promise<void> => {
    await api.delete(`/seo/card/locks/${id}`)
  },

  restoreCardRevision: async (payload: { content_id: number; revision_id: number }): Promise<unknown> =>
    unwrap(await api.post('/seo/revisions/restore', payload)),

  listCardRevisions: async (content_id: number, limit = 50): Promise<{ items: SeoRevision[] }> =>
    unwrap(await api.get('/seo/revisions', { params: { content_id, limit } })),

  getSettings: async (): Promise<SeoSettings> => unwrap(await api.get('/seo/settings')),

  saveSettings: async (payload: Partial<SeoSettings>): Promise<SeoSettings> =>
    unwrap(await api.put('/seo/settings', payload)),

  listThemes: async (params: { search?: string; limit?: number } = {}): Promise<SeoTheme[]> => {
    const data = unwrap<{ items: SeoTheme[] }>(await api.get('/seo/themes', { params }))
    return data.items ?? []
  },

  listBatchFilterOptions: async (): Promise<SeoBatchFilterOptions> =>
    unwrap(await api.get('/seo/batch-filter-options')),

  listBatchCandidates: async (params: {
    marketplace: SeoMarketplace
    seller_id: number
    theme_number?: number
    category_prefix?: string
    design_type?: string
    brand?: string
    occasion?: string
    gender?: string
    age?: string
    has_age_on_image?: boolean
    has_name_on_image?: boolean
    search?: string
    only_unpublished?: boolean
    limit?: number
    offset?: number
  }): Promise<{ items: BatchCandidate[]; total: number }> =>
    unwrap(await api.get('/seo/batch-candidates', { params })),

  previewBatch: async (payload: {
    marketplace: SeoMarketplace
    seller_id: number
    selection: SeoBatchSelection
    options: SeoBatchOptions
    sample_size?: number
  }): Promise<{ total: number; sample: BatchGenerationResult | null }> =>
    unwrap(await api.post('/seo/batch/preview', payload)),

  createBatchJob: async (payload: {
    marketplace: SeoMarketplace
    seller_id: number
    selection: SeoBatchSelection
    options: SeoBatchOptions
  }): Promise<SeoBatchJob> => unwrap(await api.post('/seo/batch/jobs', payload)),

  listBatchJobs: async (params: {
    marketplace: SeoMarketplace
    seller_id: number
    limit?: number
  }): Promise<SeoBatchJob[]> => {
    const data = unwrap<{ items: SeoBatchJob[] }>(await api.get('/seo/batch/jobs', { params }))
    return data.items || []
  },

  getBatchJob: async (id: string): Promise<SeoBatchJob> =>
    unwrap(await api.get(`/seo/batch/jobs/${id}`)),

  listStaleContents: async (params: {
    marketplace: SeoMarketplace
    seller_id: number
    limit?: number
    offset?: number
  }): Promise<{ total: number; items: SeoStaleContent[] }> =>
    unwrap(await api.get('/seo/stale-contents', { params })),

  previewBatchRollback: async (id: string): Promise<SeoBatchRollbackPreview> =>
    unwrap(await api.get(`/seo/batch/jobs/${id}/rollback`)),

  rollbackBatchJob: async (id: string): Promise<SeoBatchRollbackResult> =>
    unwrap(await api.post(`/seo/batch/jobs/${id}/rollback`)),

  listCampaigns: async (params: {
    status?: SeoCampaignStatus
    marketplace?: SeoMarketplace
    include_archived?: boolean
    limit?: number
    offset?: number
  } = {}): Promise<{ items: SeoCampaign[]; total: number }> =>
    unwrap(await api.get('/seo/campaigns', { params })),

  getCampaign: async (id: number): Promise<SeoCampaignDetails> =>
    unwrap(await api.get(`/seo/campaigns/${id}`)),

  createCampaign: async (payload: SeoCampaignPayload): Promise<SeoCampaignDetails> =>
    unwrap(await api.post('/seo/campaigns', payload)),

  updateCampaign: async (id: number, payload: SeoCampaignPayload): Promise<SeoCampaignDetails> =>
    unwrap(await api.put(`/seo/campaigns/${id}`, payload)),

  previewCampaign: async (payload: (SeoCampaignPayload | { campaign_id: number }) & {
    seller_id?: number | null
    sample_size?: number
  }): Promise<SeoCampaignPreview> => unwrap(await api.post('/seo/campaigns/preview', payload)),

  scheduleCampaign: async (id: number): Promise<SeoCampaignDetails> =>
    unwrap(await api.post(`/seo/campaigns/${id}/schedule`)),

  stopCampaign: async (id: number): Promise<SeoCampaignDetails> =>
    unwrap(await api.post(`/seo/campaigns/${id}/stop`)),

  archiveCampaign: async (id: number): Promise<{ archived: true }> =>
    unwrap(await api.post(`/seo/campaigns/${id}/archive`)),

  runCampaignPhase: async (id: number, phase: 'apply' | 'revert'): Promise<{ runs: SeoCampaignRun[] }> =>
    unwrap(await api.post(`/seo/campaigns/${id}/run`, { phase })),

  cancelBatchJob: async (id: string): Promise<SeoBatchJob> =>
    unwrap(await api.post(`/seo/batch/jobs/${id}/cancel`)),

  estimateAiBatch: async (payload: {
    marketplace: SeoMarketplace
    seller_id: number
    selection: SeoBatchSelection
    options: SeoAiBatchOptions
  }): Promise<SeoAiBatchEstimate> =>
    unwrap(await api.post('/seo/ai-batch/estimate', payload)),

  createAiBatchJob: async (payload: {
    marketplace: SeoMarketplace
    seller_id: number
    selection: SeoBatchSelection
    options: SeoAiBatchOptions
  }): Promise<SeoAiBatchJob> =>
    unwrap(await api.post('/seo/ai-batch/jobs', payload)),

  listAiBatchJobs: async (params: {
    marketplace: SeoMarketplace
    seller_id: number
    limit?: number
  }): Promise<SeoAiBatchJob[]> => {
    const data = unwrap<{ items: SeoAiBatchJob[] }>(await api.get('/seo/ai-batch/jobs', { params }))
    return data.items || []
  },

  getAiBatchJob: async (
    id: string,
    params: { result_status?: SeoAiBatchResultStatus; result_limit?: number; result_offset?: number } = {},
  ): Promise<SeoAiBatchJob> =>
    unwrap(await api.get(`/seo/ai-batch/jobs/${id}`, { params })),

  cancelAiBatchJob: async (id: string): Promise<SeoAiBatchJob> =>
    unwrap(await api.post(`/seo/ai-batch/jobs/${id}/cancel`)),

  applyAiBatchResults: async (id: string, resultIds: number[]): Promise<{
    accepted: number
    stale: number
    failed: number
    skipped: number
  }> => unwrap(await api.post(`/seo/ai-batch/jobs/${id}/apply`, { result_ids: resultIds })),

  rejectAiBatchResults: async (id: string, resultIds: number[]): Promise<{ rejected: number }> =>
    unwrap(await api.post(`/seo/ai-batch/jobs/${id}/reject`, { result_ids: resultIds })),

  checkCoverage: async (payload: {
    marketplace: SeoMarketplace
    seller_id: number
  }): Promise<{ gaps: CoverageGap[]; total_missing: number }> =>
    unwrap(await api.post('/seo/fragments/coverage', payload)),

  /** Карта покрытия: какой шаблон отвечает за категорию в каждом кабинете. */
  getTemplateCoverage: async (): Promise<SeoTemplateCoverage> =>
    unwrap(await api.get('/seo/templates-coverage')),

  listFragments: async (params: {
    marketplace?: string
    seller_id?: number
    kind?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{ items: SeoFragment[]; total: number; kinds: Array<{ kind: string; title: string }> }> =>
    unwrap(await api.get('/seo/fragments', { params })),

  saveFragment: async (payload: Partial<SeoFragment> & { marketplace: string; kind: string; text: string }): Promise<SeoFragment> =>
    unwrap(await api.post('/seo/fragments', payload)),

  archiveFragment: async (id: number): Promise<{ archived: boolean }> =>
    unwrap(await api.delete(`/seo/fragments/${id}`)),

  activateFragment: async (id: number): Promise<SeoFragment> =>
    unwrap(await api.post(`/seo/fragments/${id}/activate`)),

  listGenerators: async (params: { marketplace?: string; seller_id?: number; field?: string; status?: string } = {}): Promise<SeoGenerator[]> => {
    const data = unwrap<{ items: SeoGenerator[] }>(await api.get('/seo/generators', { params }))
    return data.items ?? []
  },

  activateGenerator: async (id: number): Promise<SeoGenerator> =>
    unwrap(await api.post(`/seo/generators/${id}/activate`)),

  saveGenerator: async (payload: Partial<SeoGenerator> & {
    marketplace: SeoMarketplace
    field: string
    name: string
    recipe: unknown
  }): Promise<SeoGenerator> => unwrap(await api.post('/seo/generators', payload)),

  previewGenerator: async (payload: {
    article: string
    marketplace: SeoMarketplace
    seller_id: number
    field: string
    recipe: unknown
  }): Promise<GenerationResult> => unwrap(await api.post('/seo/generators/preview', payload)),

  listRulesets: async (params: { marketplace?: SeoMarketplace; status?: string; scope_type?: string } = {}): Promise<SeoRuleset[]> => {
    const data = unwrap<{ items: SeoRuleset[] }>(await api.get('/seo/rulesets', { params }))
    return data.items || []
  },

  activateRuleset: async (id: number): Promise<SeoRuleset> =>
    unwrap(await api.post(`/seo/rulesets/${id}/activate`)),

  saveRuleset: async (payload: Partial<SeoRuleset> & {
    marketplace: SeoMarketplace
    scope_type: SeoRuleset['scope_type']
    name: string
    rules: Record<string, unknown>
  }): Promise<SeoRuleset> => unwrap(await api.post('/seo/rulesets', payload)),

  listKeyRules: async (params: { marketplace?: SeoMarketplace; status?: string; key_type?: string } = {}): Promise<{
    items: SeoKeyRule[]
    key_types: Array<{ key_type: string; title: string }>
  }> => unwrap(await api.get('/seo/key-rules', { params })),

  saveKeyRule: async (payload: Partial<SeoKeyRule> & {
    key_type: string
    conditions: unknown
    result_key: string
    name: string
  }): Promise<SeoKeyRule> => unwrap(await api.post('/seo/key-rules', payload)),

  archiveKeyRule: async (id: number): Promise<{ archived: boolean }> =>
    unwrap(await api.delete(`/seo/key-rules/${id}`)),

  activateKeyRule: async (id: number): Promise<SeoKeyRule> =>
    unwrap(await api.post(`/seo/key-rules/${id}/activate`)),

  listConfigurationReleases: async (params: {
    marketplace?: SeoMarketplace
    seller_id?: number
    limit?: number
  } = {}): Promise<SeoConfigurationRelease[]> => {
    const data = unwrap<{ items: SeoConfigurationRelease[] }>(await api.get('/seo/config-releases', { params }))
    return data.items || []
  },

  activateConfigurationRelease: async (payload: {
    marketplace: SeoMarketplace
    seller_id: number
    name: string
    notes?: string | null
    items: Array<{ type: SeoConfigurationEntityType; id: number }>
  }): Promise<SeoConfigurationRelease> =>
    unwrap(await api.post('/seo/config-releases', payload)),

  // ── Публикация на площадки ────────────────────────────────────────────────
  // Раздел заморожен, методы оставлены для обратимого возврата:
  // SPEC_2026-08-13_SEO_MODULE_SLIMMING.md.

  getPublishCapabilities: async (params: {
    marketplace: SeoMarketplace
    seller_id: number
  }): Promise<SeoPublishCapabilities> => unwrap(await api.get('/seo/publish/capabilities', { params })),

  previewPublish: async (payload: {
    marketplace: SeoMarketplace
    seller_id: number
    articles?: string[]
    /** Публикация к дате кампании: область и срок берутся у неё. */
    campaign_id?: number | null
    fields?: SeoPublishField[]
    force?: boolean
  }): Promise<SeoPublishPreview> => unwrap(await api.post('/seo/publish/preview', payload)),

  createPublishBatch: async (payload: {
    marketplace: SeoMarketplace
    seller_id: number
    articles?: string[]
    campaign_id?: number | null
    fields?: SeoPublishField[]
    scheduled_at?: string | null
    comment?: string | null
    force?: boolean
  }): Promise<{
    batch: SeoPublishBatch
    skipped: Array<{ article: string; reason: string | null; message: string | null }>
    dry_run: boolean
    dry_run_reason: string | null
  }> => unwrap(await api.post('/seo/publish/batches', payload)),

  listPublishBatches: async (params: {
    marketplace: SeoMarketplace
    seller_id: number
    limit?: number
  }): Promise<{ items: SeoPublishBatch[]; summary: SeoPublishSummary }> =>
    unwrap(await api.get('/seo/publish/batches', { params })),

  getPublishBatch: async (id: number, params: { status?: SeoPublishTaskStatus } = {}): Promise<{
    batch: SeoPublishBatch
    items: SeoPublishTask[]
  }> => unwrap(await api.get(`/seo/publish/batches/${id}`, { params })),

  cancelPublishBatch: async (id: number): Promise<{ cancel_requested: boolean; batch: SeoPublishBatch }> =>
    unwrap(await api.post(`/seo/publish/batches/${id}/cancel`)),

  retryPublishBatch: async (id: number): Promise<{ retried: number; batch: SeoPublishBatch }> =>
    unwrap(await api.post(`/seo/publish/batches/${id}/retry`)),

  syncPublishModeration: async (payload: {
    marketplace?: SeoMarketplace
    seller_id?: number
  } = {}): Promise<{ closed: number }> => unwrap(await api.post('/seo/publish/moderation/sync', payload)),

  getCardPublishState: async (params: { content_id: number }): Promise<SeoCardPublishState> =>
    unwrap(await api.get('/seo/card/publish-state', { params })),

  resolveDiscrepancy: async (payload: {
    content_id: number
    decision: 'keep_local' | 'accept_remote'
  }): Promise<{ content: unknown; batch: SeoPublishBatch | null; revisions: SeoRevision[] }> =>
    unwrap(await api.post('/seo/card/resolve-discrepancy', payload)),

  // ── Показатели и эффективность ────────────────────────────────────────────
  // Раздел скрыт флагом, методы оставлены по SPEC_2026-08-13_SEO_MODULE_SLIMMING.md.

  /** Состояние данных кабинета: за какой период они есть и откуда пришли. */
  getMetricsState: async (params: { seller_id: number }): Promise<SeoMetricsState> =>
    unwrap(await api.get('/seo/metrics/state', { params })),

  /** Загрузка строк из файла выгрузки или ручного ввода. */
  importMetrics: async (payload: {
    seller_id: number
    source?: 'file' | 'manual'
    rows: Array<Record<string, unknown>>
  }): Promise<SeoMetricsImportReport> => unwrap(await api.post('/seo/metrics/import', payload)),

  /** Обращение к API кабинета за показателями — только по команде оператора. */
  pullMetrics: async (payload: {
    seller_id: number
    date_from?: string
    date_to?: string
    max_requests?: number
  }): Promise<SeoMetricsImportReport> => unwrap(await api.post('/seo/metrics/pull', payload)),

  getAnalyticsOverview: async (params: {
    seller_id: number
    date_from?: string
    date_to?: string
  }): Promise<SeoAnalyticsOverview> => unwrap(await api.get('/seo/analytics/overview', { params })),

  /** Очередь «где выиграем больше всего». */
  getPotentialQueue: async (params: {
    seller_id: number
    date_from?: string
    date_to?: string
    category_prefix?: string
    min_impressions?: number
    limit?: number
    offset?: number
  }): Promise<SeoPotentialQueue> => unwrap(await api.get('/seo/analytics/potential', { params })),

  getCardAnalytics: async (params: {
    seller_id: number
    article: string
    date_from?: string
    date_to?: string
    window_days?: number
  }): Promise<SeoCardAnalytics> => unwrap(await api.get('/seo/analytics/card', { params })),

  // ── Эксперименты над формулировками ───────────────────────────────────────
  // Раздел скрыт флагом, методы оставлены по SPEC_2026-08-13_SEO_MODULE_SLIMMING.md.

  listExperiments: async (params: { seller_id?: number; status?: SeoExperimentStatus } = {}): Promise<{
    items: SeoExperiment[]
  }> => unwrap(await api.get('/seo/experiments', { params })),

  createExperiment: async (payload: SeoExperimentInput): Promise<SeoExperiment> =>
    unwrap(await api.post('/seo/experiments', payload)),

  updateExperiment: async (id: number, payload: SeoExperimentInput): Promise<SeoExperiment> =>
    unwrap(await api.put(`/seo/experiments/${id}`, payload)),

  startExperiment: async (id: number): Promise<SeoExperiment> =>
    unwrap(await api.post(`/seo/experiments/${id}/start`)),

  stopExperiment: async (id: number, conclusion?: string): Promise<SeoExperiment> =>
    unwrap(await api.post(`/seo/experiments/${id}/stop`, { conclusion })),

  /** Артикулы варианта — та же выборка, которую принимает мастер массовых изменений. */
  getExperimentVariantArticles: async (id: number, label: string): Promise<{
    label: string
    articles: string[]
    total: number
  }> => unwrap(await api.get(`/seo/experiments/${id}/articles`, { params: { label } })),

  getExperimentReport: async (id: number, params: { date_from?: string; date_to?: string } = {}): Promise<SeoExperimentReport> =>
    unwrap(await api.get(`/seo/experiments/${id}/report`, { params })),

  // ── Справочник витринных тематик для наименований МП ──────────────────────
  // Запись адресуется парой «номер + название из PIM». Просмотр — Seo:READ,
  // правка, утверждение и удаление — Seo:RULES.

  listThemeTitles: async (params: {
    status?: SeoThemeTitleStatusFilter
    search?: string
    limit?: number
    offset?: number
  } = {}): Promise<SeoThemeTitleList> =>
    unwrap(await api.get('/seo/theme-titles', { params })),

  /** Предпросмотр несохранённой записи: наименование образца без неё и с ней. */
  previewThemeTitle: async (payload: SeoThemeTitleKey & SeoThemeTitleFields): Promise<SeoThemeTitlePreviewResult> =>
    unwrap(await api.post('/seo/theme-titles/preview', payload)),

  /** `approve: false` — черновик, `true` — утверждение (сервер проверяет, что наименование собирается). */
  saveThemeTitle: async (
    payload: SeoThemeTitleKey & SeoThemeTitleFields & { approve: boolean },
  ): Promise<SeoThemeTitleEntry> => unwrap(await api.put('/seo/theme-titles', payload)),

  approveThemeTitles: async (items: SeoThemeTitleKey[]): Promise<SeoThemeTitleApproveResult> =>
    unwrap(await api.post('/seo/theme-titles/approve', { items })),

  /** Удаляет запись: тематика снова пишется так, как названа в PIM. */
  deleteThemeTitle: async (key: SeoThemeTitleKey): Promise<{ deleted: boolean }> =>
    unwrap(await api.post('/seo/theme-titles/delete', key)),

}

// ── Справочник витринных тематик ─────────────────────────────────────────────

export type SeoThemeTitleStatusFilter = 'all' | 'none' | 'draft' | 'approved'

export interface SeoThemeTitleKey {
  theme_number: number
  pim_theme: string
}

/**
 * Поля записи. У `display` пустая строка значима — «тематика целиком выражена
 * получателем или поводом», а null — «брать название из PIM». У остальных
 * полей пустая строка равносильна null.
 */
export interface SeoThemeTitleFields {
  display: string | null
  oz_display: string | null
  alias: string | null
  detail: string | null
  recipient: string | null
  recipient_female: string | null
  recipient_short: string | null
  occasion: string | null
  oz_extra: string | null
}

export interface SeoThemeTitleEntry extends SeoThemeTitleKey, SeoThemeTitleFields {
  status: 'draft' | 'approved'
  source: 'manual' | 'ai' | 'etalon'
  ai_note: string | null
  approved_by: number | null
  approved_at: string | null
  updated_by: number | null
  updated_at: string
}

export interface SeoThemeTitleSample {
  article: string
  category_prefix: string
  theme: string | null
  occasion: string | null
  gender: string | null
  age: string | null
  name_on_image: string | null
  age_on_image: number | null
  age_on_image_unit: string | null
  image_url: string | null
}

export interface SeoThemeTitleTitles {
  wb: string
  ozon: string
  error?: string
}

export interface SeoThemeTitleItem extends SeoThemeTitleKey {
  designs: number
  top_occasion: string | null
  top_gender: string | null
  entry: SeoThemeTitleEntry | null
  sample: SeoThemeTitleSample | null
  preview: null | { pim: SeoThemeTitleTitles; entry: SeoThemeTitleTitles | null }
}

export interface SeoThemeTitleList {
  items: SeoThemeTitleItem[]
  total: number
  counts: Record<SeoThemeTitleStatusFilter, number>
}

export interface SeoThemeTitlePreviewResult {
  sample: SeoThemeTitleSample | null
  pim: SeoThemeTitleTitles | null
  entry: SeoThemeTitleTitles | null
}

export interface SeoThemeTitleApproveResult {
  approved: number
  skipped: Array<SeoThemeTitleKey & { reason: string }>
}

// ── Типы раздела «Эффективность» ────────────────────────────────────────────

export interface SeoMetricTotals {
  impressions: number | null
  clicks: number | null
  cart_adds: number | null
  orders: number | null
  buyouts: number | null
  avg_price: number | null
  promo_share: number | null
  days: number
}

export interface SeoFunnelRates {
  ctr: number | null
  cart_rate: number | null
  order_rate: number | null
}

export type SeoConfidence = 'high' | 'medium' | 'low'

export interface SeoBenchmark extends SeoFunnelRates {
  category_prefix: string | null
  articles: number
  impressions: number
  reliable: boolean
}

export interface SeoMetricsImportRecord {
  id: number
  source: 'wb_api' | 'ozon_api' | 'file' | 'manual'
  date_from: string
  date_to: string
  status: 'ok' | 'partial' | 'failed'
  rows_received: number
  rows_written: number
  rows_skipped: number
  unmatched: string[] | null
  error_message: string | null
  created_at: string
}

export interface SeoMetricsCoverage {
  first_date: string | null
  last_date: string | null
  days: number
  articles: number
  sources: string[]
}

export interface SeoMetricsState {
  coverage: SeoMetricsCoverage
  imports: SeoMetricsImportRecord[]
  scope: { marketplace: SeoMarketplace; seller_id: number }
}

export interface SeoMetricsImportReport {
  import_id: number
  source: string
  period: { from: string; to: string } | null
  received: number
  written: number
  skipped: number
  unmatched: string[]
  notes: string[]
  status: 'ok' | 'partial' | 'failed'
}

export interface SeoAnalyticsOverview {
  period: { from: string; to: string }
  totals: SeoMetricTotals & { articles: number }
  rates: SeoFunnelRates
  benchmarks: SeoBenchmark[]
  overall: SeoBenchmark
  coverage: SeoMetricsCoverage
  imports: SeoMetricsImportRecord[]
  empty_reason: string | null
}

export interface SeoPotential {
  upside_orders: number | null
  upside_clicks: number | null
  rates: SeoFunnelRates
  benchmark: SeoBenchmark | null
  confidence: SeoConfidence
  weak_step: 'ctr' | 'cart' | 'order' | null
  explanation: string
}

export interface SeoPotentialItem {
  article: string
  product_title: string | null
  category_prefix: string | null
  theme_number: number | null
  theme_name: string | null
  content_id: number | null
  critical_count: number
  warning_count: number
  text_updated_at: string | null
  impressions: number | null
  clicks: number | null
  cart_adds: number | null
  orders: number | null
  potential: SeoPotential
}

export interface SeoPotentialQueue {
  items: SeoPotentialItem[]
  total: number
  period: { from: string; to: string }
  empty_reason: string | null
}

export interface SeoPeriodComparison {
  before: SeoFunnelRates
  after: SeoFunnelRates
  ctr: { before: number | null; after: number | null; change: number | null }
  cart_rate: { before: number | null; after: number | null; change: number | null }
  order_rate: { before: number | null; after: number | null; change: number | null }
  caveats: string[]
  confidence: SeoConfidence
}

export interface SeoRevisionImpact {
  revision_id: number
  revision: number
  kind: string
  effective_at: string
  is_ai: boolean
  title: string | null
  before: SeoMetricTotals
  after: SeoMetricTotals
  comparison: SeoPeriodComparison
  windows: { before: { from: string; to: string }; after: { from: string; to: string } }
}

export interface SeoCardAnalytics {
  article: string
  period: { from: string; to: string }
  totals: SeoMetricTotals
  rates: SeoFunnelRates
  series: Array<SeoMetricTotals & { metric_date: string }>
  potential: SeoPotential
  revisions: SeoRevisionImpact[]
  empty_reason: string | null
}

export type SeoExperimentStatus = 'draft' | 'running' | 'stopped' | 'finished'

export interface SeoExperimentVariant {
  id: number
  experiment_id: number
  label: string
  template_family_id: number | null
  template_version_id: number | null
  segments: number[]
  note: string | null
}

export interface SeoExperiment {
  id: number
  name: string
  marketplace: SeoMarketplace
  seller_id: number
  theme_number: number | null
  field: 'title' | 'description'
  status: SeoExperimentStatus
  hypothesis: string | null
  conclusion: string | null
  started_at: string | null
  stopped_at: string | null
  created_at: string
  updated_at: string
  variants: SeoExperimentVariant[]
}

export interface SeoExperimentInput {
  seller_id: number
  name: string
  theme_number?: number | null
  field?: 'title' | 'description'
  hypothesis?: string | null
  variants: Array<{ label: string; segments: number[]; note?: string | null }>
}

export interface SeoExperimentReport {
  experiment: SeoExperiment
  period: { from: string; to: string }
  comparison: {
    variants: Array<{
      label: string
      articles: number
      impressions: number | null
      clicks: number | null
      cart_adds: number | null
      orders: number | null
      rates: SeoFunnelRates
    }>
    baseline: string | null
    differences: Array<{ label: string; ctr: number | null; cart_rate: number | null; order_rate: number | null }>
    confidence: SeoConfidence
    caveats: string[]
    leader: string | null
  }
  empty_reason: string | null
}
